import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { PlusIcon } from "@heroicons/react/24/outline";
import SubCategoriesTableDetails from "./SubCategoriesTableDetails";

import AddSubCategoryForm from "./AddSubCategoryForm";
import {
  deleteSubCategoryById,
  fetchAllSubCategory,
} from "../../../../redux/reducer/subCategoryReducer";

const SubCategoriesSettings = () => {
  const [editingOrder, setEditingOrder] = useState(null);
  const [pricingOrder, setPricingOrder] = useState(null);
  const dispatch = useDispatch();
  const { allSubCategories, loading, error } = useSelector(
    (state) => state.subCategories
  );
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);

  const [selectedArea, setSelectedArea] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [filteredSubCategory, setFilteredSubCategory] = useState([]);

  const headers = [
    "ID",
    "Name EN",
    "Name AR",
    "Title",
    "image",
    "Area",
    "Category",
    "Actions",
  ];

  useEffect(() => {
    if (selectedArea || selectedCategory) {
      const filtered = allSubCategories.filter((subCategory) => {
        return (
          (!selectedArea || subCategory.area?._id === selectedArea) &&
          (!selectedCategory || subCategory.category?._id === selectedCategory)
        );
      });
      setFilteredSubCategory(filtered);
    } else {
      setFilteredSubCategory(allSubCategories);
    }
  }, [selectedArea, selectedCategory, allSubCategories]);

  useEffect(() => {
    const fetchData = async () => {
      // console.log("Dispatching fetchAllBrand...");
      await dispatch(fetchAllSubCategory());
    };

    fetchData();
  }, [dispatch]);

  const loadSubCategory = async () => {
    try {
      await dispatch(fetchAllSubCategory()).unwrap();
    } catch (error) {
      console.error("Failed to fetch the subCategories:", error);
    }
  };

  const handleDelete = (subCategoryId) => {
    if (window.confirm("Are you sure you want to delete this subCategory?")) {
      dispatch(deleteSubCategoryById(subCategoryId))
        .unwrap()
        .then(() => {
          alert(t("subCategoryDeleted"));
          dispatch(fetchAllSubCategory());
        })
        .catch((error) => alert(error || "Failed to delete subCategory."));
    }
  };

  const handleUpdate = () => {
    dispatch(fetchAllSubCategory());
  };

  // console.log(allSubCategories)
  return (
    <div className="mx-auto overflow-x-auto rounded-xl">
      <div className="flex justify-between my-4">
        <h1 className="text-xl font-bold text-black">SubCategories</h1>

        <div>
          <div className="flex flex-wrap gap-3 ">
            {/* Area Filter */}
            <select
              className="p-2 text-black bg-white border rounded-md disabled:bg-gray-700 disabled:text-gray-200"
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
            >
              <option value="" className="text-black">
                Filter by Area
              </option>
              {[...new Set(allSubCategories.map((b) => b.area?._id))].map(
                (id) => {
                  const name = allSubCategories.find((b) => b.area?._id === id)
                    ?.area?.name?.en;
                  return (
                    <option key={id} value={id} className="text-black">
                      {name}
                    </option>
                  );
                }
              )}
            </select>

            {/* Category Filter */}
            <select
              className="p-2 text-black bg-white border rounded-md disabled:bg-gray-700 disabled:text-gray-200 "
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              disabled={!selectedArea}
            >
              <option value="" className="text-black">
                {t("Filter by Category")}
              </option>
              {[
                ...new Set(
                  allSubCategories
                    .filter((b) => b.area?._id === selectedArea)
                    .map((b) => b.category?._id)
                ),
              ].map((id) => {
                const name = allSubCategories.find(
                  (b) => b.category?._id === id
                )?.category?.title?.en;
                return (
                  <option key={id} value={id} className="text-black">
                    {name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        <button
          className="flex items-center gap-3 px-3 py-2 duration-300 rounded-lg bg-custom-yellow hover:bg-black group"
          onClick={() => {
            setShowForm(true);
          }}
        >
          <PlusIcon className="w-6 h-6 text-black group-hover:text-white" />
          <span className="text-black group-hover:text-white">SubCategory</span>
        </button>
      </div>

      {loading ? (
        <p className="text-center text-gray-600">{t("Loading...")}</p>
      ) : error ? (
        <p className="text-center text-red-500">
          {t("Error")}: {error}
        </p>
      ) : allSubCategories?.length === 0 ? (
        <p className="text-center text-gray-500">{t("No data found")}</p>
      ) : (
        <table className="w-full my-5">
          <thead>
            <tr className="text-sm text-black bg-custom-yellow">
              {headers.map((header, index) => (
                <th key={index} className="px-2 py-2 font-bold text-center">
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredSubCategory?.map((subCategory, index) => (
              <SubCategoriesTableDetails
                key={subCategory._id || index}
                index={index + 1}
                details={{
                  id: subCategory._id,
                  number: index + 1,
                  img: subCategory.img,
                  nameEN: subCategory.name?.en,
                  nameAR: subCategory.name?.ar,
                  title: subCategory.title,
                  area: subCategory.area?._id,
                  areaName: subCategory.area?.name?.en,
                  category: subCategory.category?._id,
                  categoryName: subCategory.category?.title?.en,
                }}
                isEditing={editingOrder === subCategory._id}
                isPricing={pricingOrder === subCategory._id}
                setEditingOrder={setEditingOrder}
                setPricingOrder={setPricingOrder}
                onUpdate={handleUpdate}
                onDelete={() => handleDelete(subCategory._id)}
              />
            ))}
          </tbody>
        </table>
      )}
      {showForm && (
        <AddSubCategoryForm
          onClose={() => {
            setShowForm(false);
            loadSubCategory();
          }}
        />
      )}
    </div>
  );
};

export default SubCategoriesSettings;
